import { useState } from "react";

import { Grid } from "@mui/material";
import { findCountryByNameOrCode, type StructuredAddress } from "@simplyk/common";
import { AddressElement } from "@stripe/react-stripe-js";
import type { StripeAddressElementChangeEvent } from "@stripe/stripe-js";

import { usePrefillSearchParams } from "@/canvas/features/FormV2/helpers/usePrefillSearchParams";

const getCountryCode = (countryName: string | undefined): string => {
  if (!countryName) {
    return "";
  }
  return findCountryByNameOrCode(countryName)?.countryShortCode || "";
};

const hasTypedValue = ({ firstName, lastName, address }: StripeAddressElementChangeEvent["value"]) =>
  Boolean(firstName || lastName || address.line1 || address.city || address.postal_code);

const hasPrefill = (contactInfo: Record<string, string | undefined>) => Object.values(contactInfo).some(Boolean);

interface StripeAddressElementProps {
  addressPrefill?: StructuredAddress;
  defaultCountry: string;
}

export const StripeAddressElement = ({ addressPrefill, defaultCountry }: StripeAddressElementProps) => {
  const { contactInfo: prefillParams, isPrefillPending } = usePrefillSearchParams();
  const address = addressPrefill ?? prefillParams;
  const [isEditedBeforePrefill, setIsEditedBeforePrefill] = useState(false);
  const shouldApplySettledPrefill = !isPrefillPending && !isEditedBeforePrefill && hasPrefill(prefillParams);

  return (
    <Grid item xs={12} sx={{ position: "relative", zIndex: 9999 }}>
      <AddressElement
        key={shouldApplySettledPrefill ? "email-prefill-settled" : "awaiting-email-prefill"}
        onChange={({ value }) => {
          if (isPrefillPending && hasTypedValue(value)) {
            setIsEditedBeforePrefill(true);
          }
        }}
        options={{
          mode: "billing",
          autocomplete: { mode: "automatic" },
          blockPoBox: false,
          fields: { phone: "never" },
          display: { name: "split" },
          defaultValues: {
            firstName: prefillParams.firstName || "",
            lastName: prefillParams.lastName || "",
            address: {
              line1: address.address || "",
              city: address.city || "",
              state: address.region || "",
              postal_code: address.postalCode || "",
              country: getCountryCode(address.country || defaultCountry),
            },
          },
        }}
      />
    </Grid>
  );
};
